import { useLoading } from '@sa/hooks';
import Cookies from 'js-cookie';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import { fetchGetUserInfo, fetchLogin } from '@/service/api';
import { $t } from '@/locales';
import { useRouterPush } from '@/hooks/common/router';
import { SetupStoreId } from '@/enum';
import { useRouteStore } from '../route';
import { useTabStore } from '../tab';
import { useRoleStore } from '../roles';
import { clearAuthStorage, getToken, setToken } from './shared';

export const useAuthStore = defineStore(SetupStoreId?.Auth, () => {
  const route = useRoute();
  const routeStore = useRouteStore();
  const roleStore = useRoleStore();
  const tabStore = useTabStore();
  const { toLogin, redirectFromLogin } = useRouterPush(false);
  const { loading: loginLoading, startLoading, endLoading } = useLoading();

  const token = ref(getToken());

  // 用户信息
  const userInfo: Api.Auth.UserInfo = reactive({
    id: 0,
    phone: '',
    name: '',
    is_active: false,
    login_time: '',
    created_at: '',
    operator_name: '',
    operator: null,
    merchant: null,
    merchant_name: '',
    role: []
  });

  // 超级管理员权限判断
  const isStaticSuper = computed(() => {
    const { VITE_AUTH_ROUTE_MODE, VITE_STATIC_SUPER_ROLE } = import.meta.env;

    return VITE_AUTH_ROUTE_MODE === 'static' && userInfo?.role?.includes(VITE_STATIC_SUPER_ROLE);
  });

  // 是否登录
  const isLogin = computed(() => Boolean(token.value));

  // 重置 auth store
  async function resetStore() {
    const authStore = useAuthStore();

    clearAuthStorage();

    authStore.$reset();

    if (!route.meta.constant) {
      await toLogin();
    }

    tabStore.cacheTabs();
    routeStore.resetStore();
  }

  /**
   * 用户登录
   *
   * @param params 登录参数
   * @param [redirect=true] 是否重定向。默认是`true`. Default is `true`
   */
  const login = async (params: { phone: string; password?: string; code?: string }, redirect = true) => {
    startLoading();

    try {
      const res: any = await fetchLogin(params);
      if (res?.code === 0) {
        // 获取cookie内容
        const accessToken = Cookies.get(import.meta.env.VITE_LOGIN_COOKIE);
        setToken(accessToken as any);
        const pass = getToken();
        if (pass) {
          token.value = pass;
          await redirectFromLogin(redirect);
          window.$notification?.success({
            message: $t('page.login.common.loginSuccess'),
            description: $t('page.login.common.welcomeBack', { userName: userInfo?.name })
          });
        } else {
          resetStore();
        }
      } else {
        window.$notification?.error({
          message: $t('page.login.common.loginFailed'),
          description: res?.msg
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      endLoading();
    }
  };

  /**
   * 获取用户信息
   *
   * @returns 是否成功
   */
  async function getUserInfo() {
    const res: any = await fetchGetUserInfo();
    if (res?.code === 0) {
      Object.assign(userInfo, res.data);
      return true;
    }

    return false;
  }

  /** 初始化用户信息 */
  async function initUserInfo() {
    // 只有在已经登录的情况下才能获取用户信息
    const hasToken = getToken();

    if (hasToken) {
      const pass = await getUserInfo();
      // 获取角色信息
      await roleStore.initRoleList();
      if (!pass) {
        resetStore();
      }
    }
  }

  return {
    token,
    userInfo,
    isStaticSuper,
    isLogin,
    loginLoading,
    resetStore,
    login,
    initUserInfo
  };
});
